import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Per-category quotation signatures — one image each for Product, Service &
// Training quotes; auto-attached to that category's PDF signature block.
// Lives at the bottom of the Account & Sessions page (10.docx), admin only.
export const CategorySignaturesSection = () => {
  const [company, setCompany] = useState(null);
  const [busyCat, setBusyCat] = useState("");
  const load = () => api.get("/quotations/company").then((r) => setCompany(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const upload = async (cat, file) => {
    if (!file) return;
    setBusyCat(cat);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/quotations/company/signature/${cat}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${cat[0].toUpperCase() + cat.slice(1)} signature saved`);
      load();
    } catch (e) { toast.error(fmtError(e)); } finally { setBusyCat(""); }
  };
  const remove = async (cat) => {
    try {
      await api.delete(`/quotations/company/signature/${cat}`);
      toast.success("Signature removed");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };
  const CATS = [
    { k: "product", label: "Products" },
    { k: "service", label: "Technical Services" },
    { k: "training", label: "Training" },
  ];
  return (
    <Card className="rounded-3xl border-border/60 shadow-soft" data-testid="category-signatures-card">
      <CardContent className="p-6">
        <h2 className="font-display text-lg font-extrabold mb-1">Quotation Signatures</h2>
        <p className="text-xs text-muted-foreground mb-4">Upload a signature per category — it appears automatically in that category's quotation PDF signature block. Each category keeps its own image.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {CATS.map(({ k, label }) => {
            const url = company?.[`sig_${k}_url`];
            return (
              <div key={k} className="p-4 rounded-2xl border border-border/60" data-testid={`cat-sig-${k}`}>
                <p className="text-xs font-bold mb-2">{label}</p>
                {url ? (
                  <img src={url} alt={`${label} signature`} className="h-14 max-w-full object-contain rounded-lg border border-border/50 bg-white p-1" data-testid={`cat-sig-preview-${k}`} />
                ) : (
                  <p className="text-[11px] text-muted-foreground italic h-14 flex items-center">No signature uploaded</p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <input
                    id={`sig-file-${k}`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => { upload(k, e.target.files?.[0]); e.target.value = ""; }}
                    data-testid={`cat-sig-input-${k}`}
                  />
                  <Button variant="outline" size="sm" className="rounded-xl h-8 text-xs tap" disabled={busyCat === k}
                    onClick={() => document.getElementById(`sig-file-${k}`).click()} data-testid={`cat-sig-upload-${k}`}>
                    <Upload className="h-3 w-3 mr-1" /> {busyCat === k ? "Uploading…" : url ? "Replace" : "Upload"}
                  </Button>
                  {url && (
                    <Button variant="outline" size="sm" className="rounded-xl h-8 text-xs tap text-red-600 border-red-200" onClick={() => remove(k)} data-testid={`cat-sig-remove-${k}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
